/**
 * CoverLetterSection — the "Lettre de motivation" collapsible section of the
 * ATS sidebar. Shows the 3 AI-analysis preconditions (analyze / compare /
 * optimize), the generation config (length / fidelity / tone) and the
 * generated letter for the active version with copy + download buttons.
 *
 * Pure UI component — all state and handlers come from the parent.
 */

import { useCallback, type Dispatch, type SetStateAction } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  FileText,
  Loader2,
} from 'lucide-react';
import { saveAs } from 'file-saver';
import type { getTType } from '../../contexts/I18nContext';
import type {
  AiAnalyzeStructured,
  AiCompareStructured,
  AiAtsOptimizeStructured,
} from '../../lib/ai-types';
import type { TemplateMetaItem } from '../../hooks/useCvState';
import type {
  CoverLetterLength,
  CoverLetterFidelity,
  CoverLetterTone,
} from '../../hooks/useCoverLetter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

export interface CoverLetterSectionProps {
  t: getTType;

  // Section expand/collapse
  isExpanded: boolean;
  onToggleExpanded: () => void;

  // Active CV version
  activeTemplate: string;
  templateMeta: TemplateMetaItem[];

  // Preconditions (from useAiAnalyses)
  aiAnalysisStructured: AiAnalyzeStructured | null;
  aiCompareStructured: AiCompareStructured | null;
  aiOptimizeStructured: AiAtsOptimizeStructured | null;
  isAnalyzeStale: boolean;
  isCompareStale: boolean;
  isOptimizeStale: boolean;

  // Job description (to gate the generate button)
  effectiveJobDescription: string;

  // Cover letter config + state (from useCoverLetter)
  coverLetterLength: CoverLetterLength;
  setCoverLetterLength: Dispatch<SetStateAction<CoverLetterLength>>;
  coverLetterFidelity: CoverLetterFidelity;
  setCoverLetterFidelity: Dispatch<SetStateAction<CoverLetterFidelity>>;
  coverLetterTone: CoverLetterTone;
  setCoverLetterTone: Dispatch<SetStateAction<CoverLetterTone>>;
  generatedCoverLetterByVersionId: Record<string, string>;
  setGeneratedCoverLetterByVersionId: Dispatch<SetStateAction<Record<string, string>>>;
  isGeneratingCoverLetter: boolean;

  // Action
  onGenerateCoverLetter: () => Promise<void> | void;
}

export function CoverLetterSection(props: CoverLetterSectionProps) {
  const {
    t,
    isExpanded,
    onToggleExpanded,
    activeTemplate,
    templateMeta,
    aiAnalysisStructured,
    aiCompareStructured,
    aiOptimizeStructured,
    isAnalyzeStale,
    isCompareStale,
    isOptimizeStale,
    effectiveJobDescription,
    coverLetterLength,
    setCoverLetterLength,
    coverLetterFidelity,
    setCoverLetterFidelity,
    coverLetterTone,
    setCoverLetterTone,
    generatedCoverLetterByVersionId,
    setGeneratedCoverLetterByVersionId,
    isGeneratingCoverLetter,
    onGenerateCoverLetter,
  } = props;

  const activeVersionName =
    templateMeta.find((tm) => tm.id === activeTemplate)?.name ?? activeTemplate;

  const letterForVersion = generatedCoverLetterByVersionId[activeTemplate];

  const handleCopy = useCallback(() => {
    if (letterForVersion) {
      void navigator.clipboard.writeText(letterForVersion);
    }
  }, [letterForVersion]);

  const handleDownload = useCallback(() => {
    if (!letterForVersion) return;
    const blob = new Blob([letterForVersion], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, 'lettre-motivation.txt');
  }, [letterForVersion]);

  const isGenerateDisabled =
    isGeneratingCoverLetter ||
    !effectiveJobDescription?.trim() ||
    !aiAnalysisStructured ||
    !aiCompareStructured ||
    !aiOptimizeStructured;

  return (
    <div className="space-y-4">
      <h2
        className="flex items-center justify-between gap-2 text-base font-semibold text-white cursor-pointer hover:text-blue-300 transition-colors border-t border-white/10 pt-4"
        onClick={onToggleExpanded}
      >
        <span className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-600/20 flex items-center justify-center text-amber-400">
            <FileText size={18} />
          </div>
          {t('ats.coverLetter')}
        </span>
        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </h2>

      {isExpanded && (
        <div className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 rounded-xl border border-amber-500/20 p-4 space-y-4">
          <p className="text-[9px] text-white/50">{t('ats.coverLetterIntro')}</p>

          {/* Preconditions checklist */}
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2 text-sm">
                {aiAnalysisStructured ? (
                  <CheckCircle2 size={14} className="text-green-500 shrink-0" aria-hidden />
                ) : (
                  <AlertCircle size={14} className="text-amber-400 shrink-0" aria-hidden />
                )}
                <span className={aiAnalysisStructured ? 'text-white/80' : 'text-amber-300/90'}>
                  {t('sections.aiAnalyze')}
                </span>
              </div>
              {isAnalyzeStale && (
                <p className="text-[9px] text-amber-400/80 pl-6">{t('ats.analysisStaleHint')}</p>
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2 text-sm">
                {aiCompareStructured ? (
                  <CheckCircle2 size={14} className="text-green-500 shrink-0" aria-hidden />
                ) : (
                  <AlertCircle size={14} className="text-amber-400 shrink-0" aria-hidden />
                )}
                <span className={aiCompareStructured ? 'text-white/80' : 'text-amber-300/90'}>
                  {t('ats.compareCvOffer')}
                </span>
              </div>
              {isCompareStale && (
                <p className="text-[9px] text-amber-400/80 pl-6">{t('ats.analysisStaleHint')}</p>
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2 text-sm">
                {aiOptimizeStructured ? (
                  <CheckCircle2 size={14} className="text-green-500 shrink-0" aria-hidden />
                ) : (
                  <AlertCircle size={14} className="text-amber-400 shrink-0" aria-hidden />
                )}
                <span className={aiOptimizeStructured ? 'text-white/80' : 'text-amber-300/90'}>
                  {t('ats.optimizationAts')}
                </span>
              </div>
              {isOptimizeStale && (
                <p className="text-[9px] text-amber-400/80 pl-6">{t('ats.analysisStaleHint')}</p>
              )}
            </div>
          </div>

          <p className="text-[9px] text-white/40 border-t border-white/10 pt-2 mt-1">
            {t('ats.coverLetterForVersion')}{' '}
            <span className="text-amber-300/90 font-semibold">{activeVersionName}</span>
          </p>

          {/* Config (length / fidelity / tone) */}
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              <div>
                <label className="text-[9px] text-white/60 uppercase font-bold block mb-1">
                  {t('ats.coverLetterLength')}
                </label>
                <Select
                  value={coverLetterLength}
                  onValueChange={(v) => setCoverLetterLength(v as CoverLetterLength)}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm bg-black/20 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flash" className="text-sm">Flash (~100 mots)</SelectItem>
                    <SelectItem value="short" className="text-sm">
                      {t('ats.coverLetterLengthShort')}
                    </SelectItem>
                    <SelectItem value="standard" className="text-sm">
                      {t('ats.coverLetterLengthStandard')}
                    </SelectItem>
                    <SelectItem value="long" className="text-sm">
                      {t('ats.coverLetterLengthLong')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[9px] text-white/60 uppercase font-bold block mb-1">
                  {t('ats.coverLetterFidelity')}
                </label>
                <Select
                  value={coverLetterFidelity}
                  onValueChange={(v) => setCoverLetterFidelity(v as CoverLetterFidelity)}
                >
                  <SelectTrigger className="h-8 w-[110px] text-sm bg-black/20 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fidele" className="text-sm">
                      {t('ats.adaptationFidele')}
                    </SelectItem>
                    <SelectItem value="modere" className="text-sm">
                      {t('ats.adaptationModere')}
                    </SelectItem>
                    <SelectItem value="creatif" className="text-sm">
                      {t('ats.adaptationCreatif')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[9px] text-white/60 uppercase font-bold block mb-1">
                  {t('ats.coverLetterTone')}
                </label>
                <Select value={coverLetterTone} onValueChange={(v) => setCoverLetterTone(v as CoverLetterTone)}>
                  <SelectTrigger className="h-8 w-[130px] text-sm bg-black/20 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct" className="text-sm">Direct</SelectItem>
                    <SelectItem value="professionnel" className="text-sm">
                      {t('ats.coverLetterToneProfessional')}
                    </SelectItem>
                    <SelectItem value="dynamique" className="text-sm">Dynamique</SelectItem>
                    <SelectItem value="enthousiaste" className="text-sm">Enthousiaste</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Generate button */}
          <button
            type="button"
            onClick={() => void onGenerateCoverLetter()}
            disabled={isGenerateDisabled}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-sm font-bold transition-all disabled:opacity-50"
          >
            {isGeneratingCoverLetter ? (
              <>
                <Loader2 size={14} className="animate-spin" /> {t('ats.coverLetterGenerating')}
              </>
            ) : (
              <>
                <FileText size={14} /> {t('ats.coverLetterGenerate')}
              </>
            )}
          </button>

          {/* Generated letter (editable) */}
          {letterForVersion && (
            <div className="space-y-2 rounded-xl border border-amber-500/25 bg-black/30 overflow-hidden">
              <div className="px-3 py-2 border-b border-amber-500/20 bg-amber-500/10 flex items-center justify-between gap-2">
                <p className="text-[9px] text-amber-300 uppercase font-bold">
                  {t('ats.coverLetterResult')} — {activeVersionName}
                </p>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-sm font-bold text-white/90 flex items-center gap-1"
                  >
                    <Copy size={10} /> {t('ats.coverLetterCopy')}
                  </button>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-sm font-bold text-white/90 flex items-center gap-1"
                  >
                    <Download size={10} /> {t('ats.coverLetterDownload')}
                  </button>
                </div>
              </div>
              <div className="p-3 max-h-[280px] overflow-y-auto">
                <textarea
                  className="w-full min-h-[200px] text-sm text-white/90 bg-black/20 border border-white/10 rounded-lg p-2 resize-y font-sans leading-relaxed"
                  value={letterForVersion}
                  onChange={(e) =>
                    setGeneratedCoverLetterByVersionId((prev) => ({
                      ...prev,
                      [activeTemplate]: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
